import React from 'react';
import { cn } from '@/lib/cn';
import { CheckboxIcon, Selection } from '@/components/ui';
import { useStore } from '@/providers/StoreProvider';
import {
  getUnitAmenities,
  getBuildingAmenities,
  getPolicyAmenities,
  type AmenityConfig,
} from './filterConfigs';
import type { AmenitiesState } from '@repo/store';
import ListCell from '@/components/ui/ListCell';
import { useAnalytics } from '@/hooks/useAnalytics';

export interface AmenitiesFilterProps {
  className?: string;
}

// Shared hook for amenity filter logic - exported for reuse
export function useAmenitiesFilter() {
  const analytics = useAnalytics();
  const amenities = useStore(s => s.listingsFilters.amenities);
  const toggleAmenityFilterRaw = useStore(s => s.listingsFilters.toggleAmenityFilter);
  const resetAmenityFilter = useStore(s => s.listingsFilters.resetAmenityFilter);
  const resetAllAmenities = useStore(s => s.listingsFilters.resetAllAmenities);

  const toggleAmenityFilter = (key: keyof AmenitiesState) => {
    const eventName = String(key).startsWith('isBuilding')
      ? 'listings_filter_building_amenities'
      : 'listings_filter_unit_amenities';
    analytics.track(eventName, { val: key });
    toggleAmenityFilterRaw(key);
  };

  const isAmenitySelected = (key: keyof AmenitiesState) => {
    return amenities[key] === true;
  };

  const getAmenityValue = (key: keyof AmenitiesState) => {
    return amenities[key];
  };

  return {
    amenities,
    toggleAmenityFilter,
    resetAmenityFilter,
    resetAllAmenities,
    isAmenitySelected,
    getAmenityValue,
  };
}

// Unit Amenities Filter Component
export function UnitAmenitiesFilter({ className }: AmenitiesFilterProps) {
  const { amenities, toggleAmenityFilter } = useAmenitiesFilter();
  const unitAmenities = getUnitAmenities();

  return (
    <div className={cn('', className)}>
      <div className={cn('flex w-full flex-wrap items-start gap-2')} role="group">
        {unitAmenities.map(({ key, label, icon: IconComponent }) => {
          const selected = amenities[key] === true;
          return (
            <Selection
              key={key}
              selected={selected}
              onClick={() => toggleAmenityFilter(key)}
              leadingIcon={<IconComponent className="w-6 h-6 icon-secondary" />}
            >
              {label}
            </Selection>
          );
        })}
      </div>
    </div>
  );
}

// Building Amenities Filter Component
export function BuildingAmenitiesFilter({ className }: AmenitiesFilterProps) {
  const { amenities, toggleAmenityFilter } = useAmenitiesFilter();
  const buildingAmenities = getBuildingAmenities();

  return (
    <div className={cn('', className)}>
      <div className={cn('flex w-full flex-wrap items-start gap-2')} role="group">
        {buildingAmenities.map(({ key, label, icon: IconComponent }) => {
          const selected = amenities[key] === true;
          return (
            <Selection
              key={key}
              selected={selected}
              onClick={() => toggleAmenityFilter(key)}
              leadingIcon={<IconComponent className="w-6 h-6 icon-secondary" />}
            >
              {label}
            </Selection>
          );
        })}
      </div>
    </div>
  );
}

// Policies Filter Component
export function PoliciesFilter({ className }: AmenitiesFilterProps) {
  const { amenities, toggleAmenityFilter } = useAmenitiesFilter();
  const policyAmenities = getPolicyAmenities();

  return (
    <div className={cn('', className)}>
      {policyAmenities.map(({ key, label, icon: IconComponent }) => {
        const selected = amenities[key] === true;
        return (
          <ListCell
            key={key}
            leadingIcon={<IconComponent className="w-6 h-6 icon-secondary" />}
            title={label}
            subtitle="Verify details with the agent"
            trailing={<CheckboxIcon checked={selected} />}
            onClick={() => toggleAmenityFilter(key)}
            className="cursor-pointer"
          />
        );
      })}
    </div>
  );
}
